import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API, { fmt, SOURCES, CUSTOMER_STATUS } from '../utils/api';
import Pagination from '../components/Pagination';

const EMPTY = {
  name:'', phone:'', phone2:'', email:'', address:'', city:'', country:'',
  tax_number:'', credit_limit:0, payment_type:'cash',
  source:'مباشر', status:'active', notes:''
};

const STATUS_COLOR = { active:'badge-success', idle:'badge-warning', stopped:'badge-danger' };
const STATUS_LABEL = { active:'نشط', idle:'خامل', stopped:'متوقف' };

export default function Customers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search,    setSearch]    = useState('');
  const [fSource,   setFSource]   = useState('');
  const [fStatus,   setFStatus]   = useState('');
  const [page,      setPage]      = useState(1);
  const PER_PAGE = 50;
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [detail,    setDetail]    = useState(null);
  const [error,     setError]     = useState('');
  const [activeTab, setActiveTab] = useState('orders');

  const isAdmin   = user?.role === 'admin';
  const isManager = ['admin','sales_manager'].includes(user?.role);

  const load = () => API.get('/customers').then(r => setCustomers(r.data));
  useEffect(() => { load(); }, []);

  const openNew  = () => { setEditing(null); setForm(EMPTY); setError(''); setModal(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...c }); setError(''); setModal(true); };

  const save = async () => {
    if (!form.name.trim()) return setError('اسم العميل مطلوب');
    try {
      if (editing) await API.put(`/customers/${editing}`, form);
      else         await API.post('/customers', form);
      setModal(false); load();
    } catch(e) { setError(e.response?.data?.error || 'حدث خطأ'); }
  };

  const archive = async (id, name) => {
    if (!window.confirm(`أرشفة العميل "${name}"؟`)) return;
    await API.delete(`/customers/${id}`); load();
  };

  const loadDetail = (id) => {
    setActiveTab('orders');
    API.get(`/customers/${id}`).then(r => setDetail(r.data));
  };

  const filtered = customers.filter(c =>
    (c.name.includes(search) || (c.phone||'').includes(search)) &&
    (!fSource || c.source === fSource) &&
    (!fStatus || c.status === fStatus)
  );
  const pagedCustomers = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);
  useEffect(() => { setPage(1); }, [search, fSource, fStatus]);

  // Summary counts
  const totalDebt   = customers.reduce((s,c) => s + (c.total_debt||0), 0);
  const activeCount = customers.filter(c => c.status === 'active').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">العملاء</h1>
          <div className="page-title-sub">{customers.length} عميل · {activeCount} نشط</div>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ عميل جديد</button>
      </div>

      {/* Summary Row */}
      {isManager && totalDebt > 0 && (
        <div style={{
          background:'var(--danger-bg)', border:'1px solid #fca5a5',
          borderRadius:12, padding:'13px 18px', marginBottom:18,
          display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:'1.1rem' }}>⚠️</span>
            <div>
              <div style={{ fontWeight:700, fontSize:'0.875rem', color:'var(--danger)' }}>إجمالي الديون المستحقة</div>
              <div style={{ fontSize:'0.75rem', color:'var(--muted)', marginTop:1 }}>
                {customers.filter(c => c.total_debt > 0).length} عميل لديهم ديون
              </div>
            </div>
          </div>
          <div style={{ fontSize:'1.35rem', fontWeight:800, color:'var(--danger)' }}>{fmt.currency(totalDebt)}</div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="search-bar">
        <input className="search-input" placeholder="🔍 بحث بالاسم أو الهاتف..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <select value={fSource} onChange={e => setFSource(e.target.value)} style={{ minWidth:140 }}>
          <option value="">كل المصادر</option>
          {SOURCES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ minWidth:130 }}>
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" style={{ maxWidth:700 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{
                  width:38, height:38, borderRadius:10, flexShrink:0,
                  background:'var(--brand-light)', color:'var(--brand)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontWeight:800, fontSize:'1rem'
                }}>
                  {detail.name?.[0]}
                </div>
                <div>
                  <div className="modal-title">{detail.name}</div>
                  <span className={`badge ${STATUS_COLOR[detail.status]}`} style={{ fontSize:'0.68rem' }}>
                    {STATUS_LABEL[detail.status]}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setDetail(null)}>✕</button>
            </div>

            <div className="modal-body">
              {/* Info Grid */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:18 }}>
                {[
                  { label:'الهاتف',    value: detail.phone || '-' },
                  { label:'المدينة',   value: detail.city  || '-' },
                  { label:'البلد',     value: detail.country || '-' },
                  { label:'المصدر',    value: detail.source || '-' },
                  { label:'البريد',    value: detail.email  || '-' },
                  { label:'الديون',    value: fmt.currency(detail.orders?.reduce((s,o)=>s+(o.remaining_amount||0),0)||0), danger: true },
                ].map((item, i) => (
                  <div key={i} style={{
                    background:'var(--surface-2)', borderRadius:8,
                    padding:'8px 12px', border:'1px solid var(--border-2)'
                  }}>
                    <div style={{ fontSize:'0.68rem', color:'var(--muted)', fontWeight:700, marginBottom:2 }}>{item.label}</div>
                    <div style={{ fontWeight:600, fontSize:'0.875rem', color: item.danger ? 'var(--danger)' : 'var(--ink)' }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div className="tabs" style={{ marginBottom:14 }}>
                <button className={`tab ${activeTab==='orders'?'active':''}`} onClick={() => setActiveTab('orders')}>
                  الطلبات {detail.orders?.length > 0 && `(${detail.orders.length})`}
                </button>
                <button className={`tab ${activeTab==='tasks'?'active':''}`} onClick={() => setActiveTab('tasks')}>
                  المهام {detail.tasks?.length > 0 && `(${detail.tasks.length})`}
                </button>
              </div>

              {activeTab === 'orders' && (
                detail.orders?.length > 0 ? (
                  <div className="table-container" style={{ boxShadow:'none', border:'1px solid var(--border-2)' }}>
                    <div className="table-scroll">
                      <table>
                        <thead><tr><th>رقم الطلب</th><th>الإجمالي</th><th>المتبقي</th><th>التاريخ</th></tr></thead>
                        <tbody>
                          {detail.orders.slice(0,8).map(o => (
                            <tr key={o.id}>
                              <td style={{ fontWeight:700, fontSize:'0.84rem', color:'var(--brand)' }}>{o.order_number}</td>
                              <td>{fmt.currency(o.total)}</td>
                              <td className={o.remaining_amount > 0 ? 'text-danger font-bold' : 'text-success'}>
                                {fmt.currency(o.remaining_amount)}
                              </td>
                              <td className="text-muted" style={{ fontSize:'0.8rem' }}>{fmt.date(o.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : <div className="empty-state" style={{ padding:'28px 20px' }}><div className="empty-state-icon">🛒</div><p>لا توجد طلبات</p></div>
              )}

              {activeTab === 'tasks' && (
                detail.tasks?.length > 0 ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {detail.tasks.map(t => (
                      <div key={t.id} style={{
                        display:'flex', justifyContent:'space-between', alignItems:'center',
                        padding:'10px 13px', background:'var(--surface-2)',
                        borderRadius:8, border:'1px solid var(--border-2)'
                      }}>
                        <span style={{ fontSize:'0.875rem', fontWeight:500 }}>{t.title}</span>
                        <span className="text-muted" style={{ fontSize:'0.78rem', whiteSpace:'nowrap' }}>{fmt.date(t.due_date)}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="empty-state" style={{ padding:'28px 20px' }}><div className="empty-state-icon">📋</div><p>لا توجد مهام</p></div>
              )}
            </div>

            <div className="modal-footer">
              {isManager && (
                <button className="btn btn-secondary btn-sm" onClick={() => { setDetail(null); openEdit(detail); }}>
                  تعديل
                </button>
              )}
              <button className="btn btn-ghost btn-sm" onClick={() => setDetail(null)}>إغلاق</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'تعديل عميل' : 'عميل جديد'}</span>
              <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-grid">
                <div className="form-group span-2">
                  <label>الاسم <span className="required">*</span></label>
                  <input value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="اسم العميل الكامل" />
                </div>
                <div className="form-group">
                  <label>الهاتف</label>
                  <input value={form.phone||''} onChange={e => setForm({...form, phone:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>هاتف 2</label>
                  <input value={form.phone2||''} onChange={e => setForm({...form, phone2:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>البريد الإلكتروني</label>
                  <input value={form.email||''} onChange={e => setForm({...form, email:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>المدينة</label>
                  <input value={form.city||''} onChange={e => setForm({...form, city:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>البلد</label>
                  <input value={form.country||''} onChange={e => setForm({...form, country:e.target.value})} placeholder="مثال: السعودية، الأردن..." />
                </div>
                <div className="form-group">
                  <label>مصدر العميل</label>
                  <select value={form.source||'مباشر'} onChange={e => setForm({...form, source:e.target.value})}>
                    {SOURCES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>الحالة</label>
                  <select value={form.status||'active'} onChange={e => setForm({...form, status:e.target.value})}>
                    {Object.entries(STATUS_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>نوع الدفع</label>
                  <select value={form.payment_type||'cash'} onChange={e => setForm({...form, payment_type:e.target.value})}>
                    <option value="cash">نقدي</option>
                    <option value="credit">آجل</option>
                    <option value="both">كلاهما</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>حد الائتمان ($)</label>
                  <input type="number" value={form.credit_limit||0} onChange={e => setForm({...form, credit_limit:+e.target.value})} />
                </div>
                <div className="form-group span-2">
                  <label>العنوان</label>
                  <input value={form.address||''} onChange={e => setForm({...form, address:e.target.value})} />
                </div>
                <div className="form-group span-2">
                  <label>ملاحظات</label>
                  <textarea value={form.notes||''} onChange={e => setForm({...form, notes:e.target.value})} rows={2} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={save}>💾 حفظ</button>
              <button className="btn btn-ghost" onClick={() => setModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>العميل</th><th>الهاتف</th><th>البلد</th>
                <th>المصدر</th><th>الحالة</th><th>الديون</th>
                <th style={{ textAlign:'center' }}>الطلبات</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {pagedCustomers.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                      <div style={{
                        width:30, height:30, borderRadius:8, flexShrink:0,
                        background:'var(--brand-light)', color:'var(--brand)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontWeight:800, fontSize:'0.8rem'
                      }}>
                        {c.name?.[0]}
                      </div>
                      <span style={{ fontWeight:700, cursor:'pointer', color:'var(--brand)', fontSize:'0.875rem' }}
                        onClick={() => loadDetail(c.id)}>
                        {c.name}
                      </span>
                    </div>
                  </td>
                  <td className="text-muted" style={{ fontSize:'0.84rem' }}>{c.phone || '-'}</td>
                  <td className="text-muted" style={{ fontSize:'0.84rem' }}>{c.country || '-'}</td>
                  <td><span className="badge badge-info">{c.source || 'مباشر'}</span></td>
                  <td><span className={`badge ${STATUS_COLOR[c.status]}`}>{STATUS_LABEL[c.status]}</span></td>
                  <td>
                    {c.total_debt > 0
                      ? <span className="font-bold text-danger">{fmt.currency(c.total_debt)}</span>
                      : <span className="text-muted" style={{ fontSize:'0.8rem' }}>—</span>}
                  </td>
                  <td style={{ textAlign:'center' }}>
                    <span className="badge badge-gray">{c.orders_count}</span>
                  </td>
                  <td>
                    <div className="td-actions">
                      {isManager && (
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)}>تعديل</button>
                      )}
                      {isAdmin && (
                        <button className="btn btn-sm btn-danger btn-icon" title="أرشفة" onClick={() => archive(c.id, c.name)}>🗑</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8}>
                  <div className="empty-state">
                    <div className="empty-state-icon">👥</div>
                    <p>{search || fSource || fStatus ? 'لا توجد نتائج للبحث' : 'لا يوجد عملاء بعد'}</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onPage={setPage} />
      </div>
    </div>
  );
}
